import { Link } from 'wouter';
import { ArrowRight, Globe } from 'lucide-react';
import type { Region } from '@shared/schema';
import { getGeographyTheme } from '@shared/city-theme';

interface OnlineRegionCardProps {
  region: Region;
  onSelect: () => void;
}

export function OnlineRegionCard({ region, onSelect }: OnlineRegionCardProps) {
  const theme = getGeographyTheme('online');
  // Light-mode palette: sky blue primary, purple accent
  const lc = theme.colors; // #0284C7 primary, #7C3AED accent

  return (
    <Link
      href={`/regions/${region.slug}`}
      onClick={onSelect}
      aria-label={`Browse ${region.name}`}
      data-testid={`online-region-card-${region.slug}`}
      className="group block overflow-hidden rounded-2xl transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
      style={{
        // Sky-to-lavender gradient — expresses "digital, everywhere" without being heavy
        background: 'linear-gradient(135deg, #EFF9FF 0%, #F4EFFF 100%)',
        border: '1px solid #BAE6FD',
        boxShadow: '0 0 0 1px rgba(124,58,237,0.06), 0 4px 16px rgba(14,165,233,0.08)',
      }}
    >
      <div
        className="flex items-center gap-2.5 px-6 py-3.5"
        style={{ borderBottom: '1px solid rgba(186,230,253,0.7)' }}
      >
        <Globe className="h-4 w-4 shrink-0" style={{ color: lc.primary }} />
        <span
          className="font-condensed text-xs font-semibold uppercase tracking-widest"
          style={{ color: lc.primary }}
        >
          Online — available everywhere
        </span>
      </div>

      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3
            className="text-xl font-semibold"
            style={{ fontFamily: theme.typescale.headingStack, color: lc.foreground }}
          >
            {region.name}
          </h3>
          <p
            className="mt-1.5 max-w-prose text-sm leading-relaxed"
            style={{ color: lc.mutedForeground }}
          >
            {region.description ??
              'Live online classes you can join from anywhere. Same booking and reminder flow, no commute required.'}
          </p>
          {/* Accent pill — purple "Live & virtual" tag */}
          <div className="mt-3 inline-flex items-center gap-1.5">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: lc.accent }}
            />
            <span
              className="font-condensed text-xs font-semibold uppercase tracking-wider"
              style={{ color: lc.accent }}
            >
              Live &amp; virtual
            </span>
          </div>
        </div>

        <span
          className="inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full px-5 py-3 text-base font-semibold shadow-sm transition-transform duration-200 group-hover:translate-x-0.5"
          style={{ backgroundColor: '#0369A1', color: '#ffffff' }}
        >
          Browse all
          <ArrowRight className="h-5 w-5" />
        </span>
      </div>
    </Link>
  );
}
